<template>
  <div class="identify-take-photo">
    <div class="outer">
      <div class="camera-header">
        <button class="back-btn" type="button" @click="goBack"></button>
        <div class="camera-title">{{ current.name }}</div>
        <div class="header-space"></div>
      </div>
      <div
        ref="previewMain"
        class="main"
        @click="focusCamera"
      >
        <video
          v-show="!current.file && !cameraError"
          ref="video"
          class="camera"
          autoplay
          muted
          playsinline
          webkit-playsinline
          :style="videoZoomStyle"
        />
        <img v-if="current.file" class="taken-photo" :src="current.file">
        <div v-if="!current.file" class="focus-frame"></div>
        <div
          v-if="focusVisible && !current.file"
          class="tap-focus"
          :style="{ left: focusPoint.x + 'px', top: focusPoint.y + 'px' }"
        ></div>
        <div v-if="cameraError && !current.file" class="camera-error" @click.stop>
          <div class="camera-error-title">相机启动失败</div>
          <div class="camera-error-desc">当前 App 可能未开放网页相机权限，可重试或使用系统拍照兜底</div>
          <div class="camera-error-actions">
            <button class="camera-error-btn primary" type="button" @click.stop="takeFallbackPhoto">系统拍照</button>
            <button class="camera-error-btn" type="button" @click.stop="retryCamera">重试</button>
          </div>
        </div>
        <div v-if="!current.file" class="oper-tips">
          <div class="tip-text">{{ current.photoGuide }}</div>
        </div>
        <div v-if="!current.file && current.exampleImg" class="small-one" @click.stop="previewExampleImage">
          <div class="reference-title">拍摄示意</div>
          <img class="image-entity" :src="current.exampleImg">
        </div>
        <div v-if="!current.file && !cameraError" class="camera-debug-bar">
          <div class="zoom-badge">{{ zoomLabel }}</div>
          <select
            v-if="debugCameraDeviceOptions.length"
            class="camera-device-select"
            :value="debugSelectedDeviceId"
            :disabled="isCameraStarting"
            @click.stop
            @change="onDebugCameraChange"
          >
            <option value="" disabled>当前设备</option>
            <option
              v-for="(device, deviceIndex) in debugCameraDeviceOptions"
              :key="device.deviceId || deviceIndex"
              :value="device.deviceId"
            >
              {{ cameraDeviceLabel(device, deviceIndex) }}
            </option>
          </select>
        </div>
      </div>
      <div class="footer">
        <div class="list-wrap">
          <div class="list-left">
            <div
              v-for="(item, pointIndex) in list"
              :key="`${pointIndex}-${item.name}`"
              :class="['item', pointIndex === index ? 'is-selected' : '']"
              @click="selectPoint(pointIndex)"
            >
              <div :class="['point', item.file ? 'point-has-image' : '']">
                <img class="point-image" :src="item.file || item.stickFigureImg">
                <span v-if="isMustPoint(item)" class="must-tag">必拍</span>
                <span v-if="item.needSupplement" class="supplement-tag">重新拍摄</span>
                <span v-if="item.status === 'success'" class="point-status">✓</span>
                <span v-else-if="item.status === 'uploading' || item.status === 'singleing'" class="point-status is-processing">...</span>
                <span v-else-if="item.status === 'failed'" class="point-status is-failed">!</span>
              </div>
              <div class="point-name">{{ item.name }}</div>
            </div>
          </div>
          <div class="list-split" />
          <div
            :class="['list-right', 'appriasal-btn', isReadyToFinish ? 'ready' : 'gray']"
            @click="finishTakePhoto"
          >
            <span class="appriasal-btn--text">{{ takenCount }}/{{ list.length }}</span>
            <span class="appriasal-btn--text">{{ finishButtonText }}</span>
          </div>
        </div>
        <div v-if="current.file" class="action-wraper">
          <div class="actions-2">
            <div class="action-retake" @click="retake">重拍</div>
            <div class="action-text" @click="nextPoint">{{ hasNext ? '下一张' : '完成' }}</div>
          </div>
        </div>
        <div v-else class="action-wraper">
          <div class="actions">
            <div :class="['action-item', 'flash-action', flashOn ? 'is-on' : '']" @click="toggleFlash">
              <div class="side-icon-wrap">
                <img class="side-icon" :src="flashOn ? flashOnIcon : flashOffIcon">
              </div>
              <div class="action-label">{{ flashOn ? '已补光' : '补光' }}</div>
            </div>
            <div class="shutter" @click="takePicture"></div>
            <label class="action-item album-action">
              <input
                ref="album"
                class="album-file-input"
                type="file"
                accept="image/*"
                @change="onAlbumChange"
              >
              <img class="side-icon" :src="albumIcon">
              <div class="action-label">相册</div>
            </label>
          </div>
        </div>
      </div>
    </div>
    <input
      ref="systemCamera"
      class="hidden-input"
      type="file"
      accept="image/*"
      capture="environment"
      @change="onAlbumChange"
    >
    <canvas ref="canvas" class="hidden-input" />
  </div>
</template>

<script>
import { Toast, ImagePreview } from 'vant'
import { ALBUM_ICON, FLASH_OFF, FLASH_ON, MOCK_POINTS } from '../service-provider-access/mock-points'
import {
  queryAHSContext,
  reportAHSCameraEvent,
  submitAHSSingle,
  uploadAHSImage
} from '@/common/api/ahs-adapter'
import {
  applyHardwareZoom,
  clampZoom,
  drawZoomedVideoFrame,
  pinchDistance,
  readZoomCapability
} from './camera-zoom'
import {
  findHuaweiMainBackCamera,
  isHuaweiCameraRuntime
} from './camera-huawei'
import {
  isLikelyMainBackCamera,
  isOnePlus13TCameraRuntime,
  listOnePlus13TBackProbeCandidates,
  onePlus13TInitialZoom,
  pickOnePlus13TMainBackCamera,
  readTrackCapabilities,
  waitForCameraCapabilities
} from './camera-oneplus'
import { formatCameraDeviceLabel } from './camera-device-label'

const CAMERA_POINTS_KEY = 'service_provider_camera_points'
const CAMERA_RESULT_KEY = 'service_provider_camera_result'
const CAMERA_DEVICE_KEY = 'service_provider_camera_device_id'
const CAMERA_CAPTURE_STABLE_DELAY = 280
const CAMERA_PREVIEW_WATCHDOG_DELAY = 2000
const NATIVE_CAMERA_BRIDGE_NAMES = ['AHSCameraBridge', 'TuringCameraBridge', 'NativeCameraBridge', 'CameraBridge', 'AHSBridge', 'TuringBridge']
const NATIVE_PHOTO_CALLBACK = 'onAHSNativePhotoTaken'
const NATIVE_PHOTO_TIMEOUT = 30000

export default {
  name: 'IdentifyTakePhoto',
  data () {
    return {
      flashOnIcon: FLASH_ON,
      flashOffIcon: FLASH_OFF,
      albumIcon: ALBUM_ICON,
      list: [],
      index: 0,
      flashOn: false,
      focusVisible: false,
      focusPoint: {
        x: 0,
        y: 0
      },
      focusTimer: null,
      isCameraStarting: false,
      cameraError: '',
      cameraTaskInfo: {},
      enteredFromAccess: false,
      stream: null,
      cameraDeviceOptions: [],
      allCameraDeviceOptions: [],
      selectedCameraDeviceId: '',
      currentCameraDeviceId: '',
      nativePhotoCallbackName: '',
      nativePhotoTimer: null,
      nativeCameraWaiting: false,
      missingCameraApiReported: false,
      previewInactiveReported: false,
      previewWatchdogTimer: null,
      zoomValue: 1,
      zoomMin: 1,
      zoomMax: 1,
      zoomHardware: false,
      pinchStartDistance: 0,
      pinchStartZoom: 1,
      isPinching: false,
      pinchMoved: false,
      previewZoomBound: false,
      huaweiMainCameraTried: false,
      onePlus13TMainCameraTried: false
    }
  },
  computed: {
    current () {
      return this.list[this.index] || {
        name: '拍照',
        photoGuide: '请等待鉴定点位加载',
        exampleImg: '',
        stickFigureImg: ''
      }
    },
    takenCount () {
      return this.list.filter((item) => item.file).length
    },
    missingCount () {
      return Math.max(this.list.length - this.takenCount, 0)
    },
    mustPoints () {
      return this.list.filter((item) => this.isMustPoint(item))
    },
    missingMustCount () {
      return this.mustPoints.filter((item) => !item.file).length
    },
    processingMustCount () {
      return this.mustPoints.filter((item) => item.status === 'uploading' || item.status === 'singleing').length
    },
    failedMustCount () {
      return this.mustPoints.filter((item) => item.status === 'failed').length
    },
    pendingMustResultCount () {
      return this.mustPoints.filter((item) => item.file && !item.said && item.status !== 'uploading' && item.status !== 'singleing' && item.status !== 'failed').length
    },
    processingCount () {
      return this.list.filter((item) => item.status === 'uploading' || item.status === 'singleing').length
    },
    isReadyToFinish () {
      return this.list.length > 0 && this.missingMustCount === 0 && this.processingMustCount === 0 && this.failedMustCount === 0 && this.pendingMustResultCount === 0
    },
    finishButtonText () {
      return this.processingCount ? '鉴定中' : '去鉴别'
    },
    hasNext () {
      return this.index < this.list.length - 1
    },
    internalTaskNo () {
      return this.$route.query.internalTaskNo || this.cameraTaskInfo.internalTaskNo || ''
    },
    partnerTaskNo () {
      return this.$route.query.partnerTaskNo || this.$route.query.taskNo || this.cameraTaskInfo.taskNo || ''
    },
    token () {
      return this.$route.query.token || this.cameraTaskInfo.token || ''
    },
    taskParams () {
      return {
        internalTaskNo: this.internalTaskNo,
        taskNo: this.partnerTaskNo,
        token: this.token
      }
    },
    cameraDebugEnabled () {
      return this.$route.query.debug === '1' || this.$route.query.cameraDebug === '1' || this.$route.query.debugCamera === '1'
    },
    debugCameraDeviceOptions () {
      return this.allCameraDeviceOptions.length ? this.allCameraDeviceOptions : this.cameraDeviceOptions
    },
    debugSelectedDeviceId () {
      const matched = this.matchCameraDeviceOption(this.currentVideoTrack(), this.debugCameraDeviceOptions)
      if (matched && matched.deviceId) {
        return matched.deviceId
      }
      if (this.currentCameraDeviceId && this.debugCameraDeviceOptions.some((device) => device.deviceId === this.currentCameraDeviceId)) {
        return this.currentCameraDeviceId
      }
      return ''
    },
    emptyVideoSimulate () {
      return this.$route.query.emptyVideo === '1' || this.$route.query.simulateEmptyVideo === '1'
    },
    zoomLabel () {
      return `${this.zoomValue.toFixed(1)}x`
    },
    videoZoomStyle () {
      if (this.zoomHardware || this.zoomValue <= 1) {
        return undefined
      }
      return { transform: `scale(${this.zoomValue})` }
    }
  },
  async mounted () {
    await this.loadCameraPoints()
    const pointIndex = Number(this.$route.query.pointIndex)
    const safeIndex = isNaN(pointIndex) ? 0 : pointIndex
    this.index = this.list.length ? Math.min(Math.max(safeIndex, 0), this.list.length - 1) : 0
    this.bindCameraLifecycle()
    this.selectedCameraDeviceId = this.loadPreferredCameraDevice()
    this.startCamera()
  },
  activated () {
    this.resumeCamera()
  },
  deactivated () {
    this.stopCamera()
  },
  beforeDestroy () {
    this.unbindCameraLifecycle()
    this.unregisterNativePhotoCallback()
    if (this.flashOn) {
      this.callNativeTorch(false)
    }
    this.stopCamera()
  },
  beforeRouteEnter (to, from, next) {
    next((vm) => {
      vm.enteredFromAccess = from && from.name === 'serviceProviderAccess'
    })
  },
  methods: {
    async startCamera () {
      this.cameraLog('startCamera', {
        isCameraStarting: this.isCameraStarting,
        hasFile: !!this.current.file,
        hasMediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      })
      if (this.isCameraStarting || this.current.file) {
        return
      }
      if (this.emptyVideoSimulate) {
        this.cameraLog('simulate empty video')
        this.schedulePreviewWatchdog()
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = '当前环境不支持网页相机'
        if (!this.missingCameraApiReported) {
          this.missingCameraApiReported = true
          this.reportCameraEvent('camera_api_unavailable', {
            reason: 'media_devices_or_get_user_media_missing',
            hasMediaDevicesObject: !!(navigator && navigator.mediaDevices),
            hasGetUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
            protocol: (window.location && window.location.protocol) || ''
          })
        }
        return
      }
      this.isCameraStarting = true
      this.cameraError = ''
      this.stopCamera()
      try {
        const stream = await this.preferOnePlus13TMainCamera(await this.preferHuaweiMainCamera(await this.getCameraStream()))
        this.stream = stream
        this.cameraLog('stream ready', this.streamInfo(stream))
        this.$nextTick(() => {
          const video = this.$refs.video
          if (!video) {
            this.cameraLog('video ref missing')
            return
          }
          video.muted = true
          video.setAttribute('playsinline', 'true')
          video.setAttribute('webkit-playsinline', 'true')
          video.srcObject = stream
          video.onloadedmetadata = () => {
            this.cameraLog('loadedmetadata', this.videoInfo(video))
            this.playVideo()
            this.applyAutoFocus()
          }
          this.playVideo()
          this.applyAutoFocus()
          this.bindCameraZoom(stream)
          this.syncCurrentCameraDevice(stream)
        })
      } catch (err) {
        this.cameraError = err && err.message ? err.message : '相机启动失败'
        this.cameraLog('startCamera error', this.errorInfo(err))
        this.reportCameraEvent('camera_start_failed', {
          error: this.errorInfo(err),
          constraintsList: this.cameraConstraintsList()
        })
      } finally {
        this.isCameraStarting = false
        this.schedulePreviewWatchdog()
      }
    },
    getCameraStream () {
      return this.getCameraStreamByConstraints(this.cameraConstraintsList())
    },
    async preferHuaweiMainCamera (stream) {
      if (!isHuaweiCameraRuntime()) {
        return stream
      }
      if (this.huaweiMainCameraTried) {
        return stream
      }
      this.huaweiMainCameraTried = true
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return stream
      }
      let devices = []
      try {
        devices = await navigator.mediaDevices.enumerateDevices()
      } catch (err) {
        this.reportCameraEvent('huawei_main_camera_unavailable', {
          reason: 'enumerate_failed',
          error: this.errorInfo(err)
        })
        return stream
      }
      const main = findHuaweiMainBackCamera(devices.filter((device) => device.kind === 'videoinput'))
      if (!main) {
        this.reportCameraEvent('huawei_main_camera_unavailable', {
          reason: 'camera2_0_not_found',
          labels: devices.filter((device) => device.kind === 'videoinput').map((device) => device.label)
        })
        return stream
      }
      const currentTrack = stream && stream.getVideoTracks()[0]
      const currentId = currentTrack && currentTrack.getSettings ? currentTrack.getSettings().deviceId : ''
      if (currentId && currentId === main.deviceId) {
        this.cameraLog('preferHuaweiMainCamera already main', { deviceId: main.deviceId, label: main.label })
        return stream
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      try {
        const nextStream = await this.openCameraByDeviceId(main.deviceId)
        this.selectedCameraDeviceId = main.deviceId
        this.savePreferredCameraDevice(main.deviceId)
        this.reportCameraEvent('huawei_switched_main_camera', {
          deviceId: main.deviceId,
          label: main.label,
          previousDeviceId: currentId
        })
        return nextStream
      } catch (err) {
        this.reportCameraEvent('huawei_switch_failed', {
          deviceId: main.deviceId,
          label: main.label,
          error: this.errorInfo(err)
        })
        return this.getCameraStream()
      }
    },
    async preferOnePlus13TMainCamera (stream) {
      if (!isOnePlus13TCameraRuntime()) {
        return stream
      }
      if (this.onePlus13TMainCameraTried) {
        return stream
      }
      this.onePlus13TMainCameraTried = true
      await waitForCameraCapabilities()
      const currentTrack = stream && stream.getVideoTracks()[0]
      const currentCaps = readTrackCapabilities(currentTrack)
      const currentId = currentTrack && currentTrack.getSettings ? currentTrack.getSettings().deviceId : ''
      if (isLikelyMainBackCamera(currentCaps)) {
        this.reportCameraEvent('oneplus_13t_keep_current_camera', {
          reason: 'current_has_torch',
          currentDeviceId: currentId,
          capabilities: currentCaps
        })
        return stream
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return stream
      }
      let devices = []
      try {
        devices = await navigator.mediaDevices.enumerateDevices()
      } catch (err) {
        this.reportCameraEvent('oneplus_13t_main_camera_unavailable', {
          reason: 'enumerate_failed',
          error: this.errorInfo(err)
        })
        return stream
      }
      const videoDevices = devices.filter((device) => device.kind === 'videoinput')
      const main = pickOnePlus13TMainBackCamera(videoDevices, currentId, currentCaps)
      if (main) {
        return this.switchOnePlus13TCamera(stream, main, currentId, 'capability_score')
      }
      const candidates = listOnePlus13TBackProbeCandidates(videoDevices, currentId).slice(0, 2)
      for (let i = 0; i < candidates.length; i += 1) {
        const nextStream = await this.probeOnePlus13TCamera(stream, candidates[i], currentId)
        if (nextStream) {
          return nextStream
        }
      }
      this.reportCameraEvent('oneplus_13t_keep_current_camera', {
        reason: 'no_torch_back_camera',
        currentDeviceId: currentId,
        capabilities: currentCaps,
        labels: videoDevices.map((device) => device.label)
      })
      return stream
    },
    async switchOnePlus13TCamera (stream, device, previousDeviceId, reason) {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      try {
        const nextStream = await this.openCameraByDeviceId(device.deviceId)
        this.selectedCameraDeviceId = device.deviceId
        this.savePreferredCameraDevice(device.deviceId)
        this.reportCameraEvent('oneplus_13t_switched_main_camera', {
          reason,
          deviceId: device.deviceId,
          label: device.label,
          previousDeviceId
        })
        return nextStream
      } catch (err) {
        this.reportCameraEvent('oneplus_13t_switch_failed', {
          deviceId: device.deviceId,
          label: device.label,
          error: this.errorInfo(err)
        })
        return this.getCameraStream()
      }
    },
    async probeOnePlus13TCamera (stream, device, previousDeviceId) {
      let probeStream
      try {
        probeStream = await this.openCameraByDeviceId(device.deviceId)
        await waitForCameraCapabilities()
        const caps = readTrackCapabilities(probeStream.getVideoTracks()[0])
        if (!isLikelyMainBackCamera(caps)) {
          probeStream.getTracks().forEach((track) => track.stop())
          return null
        }
        if (stream) {
          stream.getTracks().forEach((track) => track.stop())
        }
        this.selectedCameraDeviceId = device.deviceId
        this.savePreferredCameraDevice(device.deviceId)
        this.reportCameraEvent('oneplus_13t_switched_main_camera', {
          reason: 'torch_probe',
          deviceId: device.deviceId,
          label: device.label,
          previousDeviceId,
          capabilities: caps
        })
        return probeStream
      } catch (err) {
        if (probeStream) {
          probeStream.getTracks().forEach((track) => track.stop())
        }
        this.reportCameraEvent('oneplus_13t_switch_failed', {
          deviceId: device.deviceId,
          label: device.label,
          error: this.errorInfo(err)
        })
        return null
      }
    },
    openCameraByDeviceId (deviceId) {
      return this.getCameraStreamByConstraints([
        {
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            zoom: true
          },
          audio: false
        },
        {
          video: {
            deviceId: { exact: deviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        }
      ])
    },
    async getCameraStreamByConstraints (constraintsList) {
      let lastError
      for (let i = 0; i < constraintsList.length; i += 1) {
        const constraints = constraintsList[i]
        try {
          this.cameraLog('getUserMedia try', { index: i, constraints })
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          this.cameraLog('getUserMedia success', { index: i, stream: this.streamInfo(stream) })
          return stream
        } catch (err) {
          this.cameraLog('getUserMedia failed', { index: i, error: this.errorInfo(err) })
          this.reportCameraEvent('get_user_media_failed', {
            index: i,
            error: this.errorInfo(err),
            constraints
          })
          lastError = err
        }
      }
      throw lastError || new Error('相机启动失败')
    },
    cameraConstraintsList () {
      const preferredDeviceId = this.selectedCameraDeviceId || this.loadPreferredCameraDevice()
      const videoOptions = []
      if (preferredDeviceId) {
        videoOptions.push({
          deviceId: { exact: preferredDeviceId },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        })
      }
      videoOptions.push({
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      })
      videoOptions.push({
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 1920 }
      })
      videoOptions.push({
        facingMode: 'environment'
      })
      const constraints = []
      videoOptions.forEach((video) => {
        constraints.push({ video: Object.assign({}, video, { zoom: true }), audio: false })
      })
      videoOptions.forEach((video) => {
        constraints.push({ video, audio: false })
      })
      constraints.push({
        video: true,
        audio: false
      })
      return constraints
    },
    async refreshCameraDevices () {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter((device) => device.kind === 'videoinput')
        const backDevices = videoDevices.filter((device) => this.isBackCameraDevice(device))
        const options = backDevices.length ? backDevices : videoDevices
        this.allCameraDeviceOptions = videoDevices
        this.cameraDeviceOptions = this.sortCameraDevices(options)
        this.cameraLog('refreshCameraDevices', {
          selectedCameraDeviceId: this.selectedCameraDeviceId,
          currentCameraDeviceId: this.currentCameraDeviceId,
          devices: this.cameraDeviceOptions.map((device) => ({
            deviceId: device.deviceId,
            label: device.label,
            groupId: device.groupId
          }))
        })
      } catch (err) {
        this.cameraLog('refreshCameraDevices failed', this.errorInfo(err))
        this.reportCameraEvent('camera_devices_failed', {
          error: this.errorInfo(err)
        })
      }
    },
    isBackCameraDevice (device) {
      const label = String(device.label || '').toLowerCase()
      if (!label) {
        return false
      }
      return /back|rear|environment|后|背|wide|tele|camera\s*0|camera\s*2/.test(label) && !/front|user|前/.test(label)
    },
    cameraDeviceLabel (device, index) {
      return formatCameraDeviceLabel(device, index)
    },
    async onDebugCameraChange (event) {
      const deviceId = event && event.target ? event.target.value : ''
      if (!deviceId || deviceId === this.debugSelectedDeviceId || this.isCameraStarting) {
        return
      }
      this.isCameraStarting = true
      try {
        this.stopCamera()
        const stream = await this.openCameraByDeviceId(deviceId)
        this.selectedCameraDeviceId = deviceId
        this.savePreferredCameraDevice(deviceId)
        await this.attachCameraStream(stream)
        await this.refreshCameraDevices()
        this.reportCameraEvent('debug_switched_camera', {
          deviceId,
          label: this.cameraDeviceLabel({ deviceId, label: (this.debugCameraDeviceOptions.find((item) => item.deviceId === deviceId) || {}).label }, 0)
        })
      } catch (err) {
        this.cameraError = err && err.message ? err.message : '切换摄像头失败'
        this.reportCameraEvent('debug_switch_camera_failed', {
          deviceId,
          error: this.errorInfo(err)
        })
      } finally {
        this.isCameraStarting = false
      }
    },
    sortCameraDevices (devices) {
      const currentId = this.currentCameraDeviceId
      const preferredId = this.selectedCameraDeviceId || this.loadPreferredCameraDevice()
      return [...devices].sort((a, b) => {
        if (a.deviceId === currentId) return -1
        if (b.deviceId === currentId) return 1
        if (a.deviceId === preferredId) return -1
        if (b.deviceId === preferredId) return 1
        return 0
      })
    },
    async syncCurrentCameraDevice (stream) {
      await this.refreshCameraDevices()
      this.rememberCurrentCamera(stream)
      if (this.currentCameraDeviceId) {
        return
      }
      await waitForCameraCapabilities()
      this.rememberCurrentCamera(stream || this.stream)
      await this.refreshCameraDevices()
    },
    matchCameraDeviceOption (track, devices) {
      const options = devices || []
      if (!track || !options.length) {
        return null
      }
      const settings = track.getSettings ? track.getSettings() : {}
      const trackLabel = String(track.label || settings.label || '').trim()
      if (trackLabel) {
        const byLabel = options.find((device) => String(device.label || '').trim() === trackLabel)
        if (byLabel) {
          return byLabel
        }
      }
      if (settings.deviceId) {
        const byId = options.find((device) => device.deviceId === settings.deviceId)
        if (byId) {
          return byId
        }
      }
      if (settings.groupId) {
        const byGroup = options.filter((device) => device.groupId === settings.groupId)
        if (byGroup.length === 1) {
          return byGroup[0]
        }
      }
      return null
    },
    rememberCurrentCamera (stream) {
      const track = stream && stream.getVideoTracks()[0]
      const settings = track && track.getSettings ? track.getSettings() : {}
      const matched = this.matchCameraDeviceOption(track, this.debugCameraDeviceOptions)
      const deviceId = (matched && matched.deviceId) || settings.deviceId || ''
      if (!deviceId) {
        return
      }
      this.currentCameraDeviceId = deviceId
      this.selectedCameraDeviceId = deviceId
      this.savePreferredCameraDevice(deviceId)
      this.cameraLog('rememberCurrentCamera', {
        deviceId,
        label: (matched && matched.label) || track.label || '',
        settings
      })
    },
    bindCameraZoom (stream) {
      const track = stream && stream.getVideoTracks()[0]
      const capability = readZoomCapability(track)
      const initialZoom = isOnePlus13TCameraRuntime()
        ? onePlus13TInitialZoom(capability)
        : capability.min
      this.zoomHardware = capability.supported
      this.zoomMin = capability.min
      this.zoomMax = capability.max
      this.zoomValue = initialZoom
      this.pinchStartZoom = initialZoom
      this.cameraLog('bindCameraZoom', { ...capability, initialZoom })
      if (capability.supported) {
        applyHardwareZoom(track, initialZoom)
      }
      if (isOnePlus13TCameraRuntime() && initialZoom !== capability.min) {
        this.reportCameraEvent('oneplus_13t_normalized_zoom', {
          min: capability.min,
          max: capability.max,
          zoom: initialZoom
        })
      }
    },
    resetCameraZoom () {
      this.zoomValue = 1
      this.zoomMin = 1
      this.zoomMax = 1
      this.zoomHardware = false
      this.pinchStartDistance = 0
      this.pinchStartZoom = 1
      this.isPinching = false
    },
    setCameraZoom (nextZoom) {
      const zoom = clampZoom(nextZoom, this.zoomMin, this.zoomMax)
      this.zoomValue = Number(zoom.toFixed(2))
      if (!this.zoomHardware) {
        return
      }
      const track = this.stream && this.stream.getVideoTracks()[0]
      applyHardwareZoom(track, this.zoomValue).then((ok) => {
        if (!ok) {
          this.zoomHardware = false
          this.zoomMax = Math.max(this.zoomMax, 3)
          this.cameraLog('hardware zoom fallback to css', { zoom: this.zoomValue })
        }
      })
    },
    bindPreviewZoomGestures () {
      const preview = this.$refs.previewMain
      if (!preview || this.previewZoomBound) {
        return
      }
      preview.addEventListener('touchstart', this.onPreviewTouchStart, { passive: false })
      preview.addEventListener('touchmove', this.onPreviewTouchMove, { passive: false })
      preview.addEventListener('touchend', this.onPreviewTouchEnd)
      preview.addEventListener('touchcancel', this.onPreviewTouchEnd)
      preview.addEventListener('wheel', this.onPreviewWheel, { passive: false })
      this.previewZoomBound = true
    },
    unbindPreviewZoomGestures () {
      const preview = this.$refs.previewMain
      if (!preview || !this.previewZoomBound) {
        return
      }
      preview.removeEventListener('touchstart', this.onPreviewTouchStart)
      preview.removeEventListener('touchmove', this.onPreviewTouchMove)
      preview.removeEventListener('touchend', this.onPreviewTouchEnd)
      preview.removeEventListener('touchcancel', this.onPreviewTouchEnd)
      preview.removeEventListener('wheel', this.onPreviewWheel)
      this.previewZoomBound = false
    },
    onPreviewTouchStart (event) {
      if (this.current.file || this.cameraError || !event.touches || event.touches.length < 2) {
        return
      }
      this.isPinching = true
      this.pinchMoved = true
      this.pinchStartDistance = pinchDistance(event.touches)
      this.pinchStartZoom = this.zoomValue
      event.preventDefault()
    },
    onPreviewTouchMove (event) {
      if (!this.isPinching || !event.touches || event.touches.length < 2 || !this.pinchStartDistance) {
        return
      }
      event.preventDefault()
      const ratio = pinchDistance(event.touches) / this.pinchStartDistance
      this.setCameraZoom(this.pinchStartZoom * ratio)
    },
    onPreviewTouchEnd (event) {
      if (!event.touches || event.touches.length < 2) {
        this.isPinching = false
        this.pinchStartDistance = 0
      }
    },
    onPreviewWheel (event) {
      if (this.current.file || this.cameraError) {
        return
      }
      event.preventDefault()
      const range = Math.max(this.zoomMax - this.zoomMin, 1)
      const step = event.ctrlKey ? range * 0.04 : range * 0.08
      const next = event.deltaY > 0 ? this.zoomValue - step : this.zoomValue + step
      this.setCameraZoom(next)
    },
    loadPreferredCameraDevice () {
      try {
        return localStorage.getItem(CAMERA_DEVICE_KEY) || ''
      } catch (err) {
        return ''
      }
    },
    savePreferredCameraDevice (deviceId) {
      try {
        localStorage.setItem(CAMERA_DEVICE_KEY, deviceId)
      } catch (err) {}
    },
    resumeCamera () {
      this.cameraLog('resumeCamera', {
        hasFile: !!this.current.file,
        hasStream: !!this.stream,
        video: this.videoInfo(this.$refs.video)
      })
      if (this.current.file) {
        return
      }
      const video = this.$refs.video
      if (!this.stream || !video || video.readyState < 2 || video.paused) {
        this.startCamera()
        return
      }
      this.playVideo()
      this.applyAutoFocus()
    },
    retryCamera () {
      this.cameraLog('retryCamera')
      this.missingCameraApiReported = false
      this.previewInactiveReported = false
      this.startCamera()
    },
    playVideo () {
      const video = this.$refs.video
      if (!video) {
        this.cameraLog('playVideo skipped: video missing')
        return
      }
      const playPromise = video.play()
      if (playPromise && playPromise.catch) {
        playPromise.then(() => {
          this.cameraLog('playVideo success', this.videoInfo(video))
        }).catch((err) => {
          this.cameraLog('playVideo failed', this.errorInfo(err))
          this.reportCameraEvent('camera_play_failed', {
            error: this.errorInfo(err),
            video: this.videoInfo(video)
          })
          this.startCamera()
        })
      }
    },
    bindCameraLifecycle () {
      document.addEventListener('visibilitychange', this.handleVisibilityChange)
      window.addEventListener('pageshow', this.resumeCamera)
      window.addEventListener('pagehide', this.stopCamera)
      this.bindPreviewZoomGestures()
    },
    unbindCameraLifecycle () {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange)
      window.removeEventListener('pageshow', this.resumeCamera)
      window.removeEventListener('pagehide', this.stopCamera)
      this.unbindPreviewZoomGestures()
      if (this.focusTimer) {
        clearTimeout(this.focusTimer)
        this.focusTimer = null
      }
      if (this.nativePhotoTimer) {
        clearTimeout(this.nativePhotoTimer)
        this.nativePhotoTimer = null
      }
      this.clearPreviewWatchdog()
    },
    handleVisibilityChange () {
      this.cameraLog('visibilitychange', { hidden: document.hidden })
      if (document.hidden) {
        this.stopCamera()
        return
      }
      this.$nextTick(() => {
        this.resumeCamera()
      })
    },
    applyAutoFocus () {
      const track = this.stream && this.stream.getVideoTracks()[0]
      if (!track || !track.getCapabilities || !track.applyConstraints) {
        this.cameraLog('applyAutoFocus skipped', { hasTrack: !!track })
        return
      }
      const caps = track.getCapabilities()
      const advanced = []
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
        advanced.push({ focusMode: 'continuous' })
      }
      if (Array.isArray(caps.exposureMode) && caps.exposureMode.includes('continuous')) {
        advanced.push({ exposureMode: 'continuous' })
      }
      if (Array.isArray(caps.whiteBalanceMode) && caps.whiteBalanceMode.includes('continuous')) {
        advanced.push({ whiteBalanceMode: 'continuous' })
      }
      if (advanced.length) {
        this.cameraLog('applyAutoFocus constraints', { advanced })
        track.applyConstraints({ advanced }).catch((err) => {
          this.cameraLog('applyAutoFocus failed', this.errorInfo(err))
          this.reportCameraEvent('camera_auto_focus_failed', {
            error: this.errorInfo(err),
            advanced,
            track: this.trackInfo(track)
          })
        })
      }
    },
    async focusCamera (event) {
      if (this.current.file || !this.stream || this.isPinching || this.pinchMoved) {
        this.pinchMoved = false
        return
      }
      const main = event.currentTarget
      const rect = main.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      this.showFocusPoint(x, y)

      const track = this.stream.getVideoTracks()[0]
      if (!track) {
        this.cameraLog('focusCamera skipped: track missing')
        return
      }
      this.cameraLog('focusCamera', { x, y, rect: { width: rect.width, height: rect.height } })
      if (window.ImageCapture) {
        try {
          const imageCapture = new window.ImageCapture(track)
          if (imageCapture.setOptions) {
            await imageCapture.setOptions({
              pointsOfInterest: [{ x: x / rect.width, y: y / rect.height }]
            })
            this.cameraLog('ImageCapture setOptions success')
          }
        } catch (err) {
          this.cameraLog('ImageCapture setOptions failed', this.errorInfo(err))
          this.reportCameraEvent('camera_tap_focus_failed', {
            error: this.errorInfo(err),
            x,
            y,
            rect: { width: rect.width, height: rect.height },
            track: this.trackInfo(track)
          })
        }
      }
      setTimeout(() => {
        this.applyAutoFocus()
      }, 300)
    },
    showFocusPoint (x, y) {
      this.focusPoint = { x, y }
      this.focusVisible = true
      if (this.focusTimer) {
        clearTimeout(this.focusTimer)
      }
      this.focusTimer = setTimeout(() => {
        this.focusVisible = false
      }, 800)
    },
    schedulePreviewWatchdog () {
      this.clearPreviewWatchdog()
      this.previewWatchdogTimer = setTimeout(() => {
        this.checkCameraPreview()
      }, CAMERA_PREVIEW_WATCHDOG_DELAY)
    },
    clearPreviewWatchdog () {
      if (this.previewWatchdogTimer) {
        clearTimeout(this.previewWatchdogTimer)
        this.previewWatchdogTimer = null
      }
    },
    checkCameraPreview () {
      if (this.current.file || this.isCameraStarting || document.hidden) {
        return
      }
      const video = this.$refs.video
      const reason = this.cameraPreviewInactiveReason(video)
      if (!reason) {
        return
      }
      if (this.cameraError && !this.emptyVideoSimulate) {
        return
      }
      if (this.previewInactiveReported) {
        return
      }
      this.previewInactiveReported = true
      this.reportCameraEvent('camera_preview_inactive', {
        reason,
        video: this.videoInfo(video),
        hasStream: !!this.stream,
        stream: this.streamInfo(this.stream)
      })
      if (!this.emptyVideoSimulate) {
        this.cameraError = '相机预览未启动'
      }
    },
    cameraPreviewInactiveReason (video) {
      if (!this.stream) {
        return 'stream_missing'
      }
      if (!video) {
        return 'video_missing'
      }
      if (!video.srcObject) {
        return 'src_object_missing'
      }
      if (video.paused) {
        return 'video_paused'
      }
      if (!video.videoWidth) {
        return 'video_width_zero'
      }
      return ''
    },
    stopCamera () {
      this.cameraLog('stopCamera', { hasStream: !!this.stream })
      this.resetCameraZoom()
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop())
        this.stream = null
      }
      const video = this.$refs.video
      if (video) {
        video.pause()
        video.srcObject = null
        video.onloadedmetadata = null
      }
    },
    cameraLog (message, data) {
      if (!this.cameraDebugEnabled || !window.console) {
        return
      }
      const payload = {
        point: this.current && this.current.name,
        index: this.index,
        internalTaskNo: this.internalTaskNo,
        ...data
      }
      console.log(`[AHS Camera] ${message}`, payload)
    },
    streamInfo (stream) {
      if (!stream) {
        return null
      }
      return {
        active: stream.active,
        tracks: stream.getTracks().map((track) => ({
          kind: track.kind,
          label: track.label,
          readyState: track.readyState,
          enabled: track.enabled,
          settings: track.getSettings ? track.getSettings() : {}
        }))
      }
    },
    videoInfo (video) {
      if (!video) {
        return null
      }
      return {
        readyState: video.readyState,
        paused: video.paused,
        ended: video.ended,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        hasSrcObject: !!video.srcObject
      }
    },
    errorInfo (err) {
      if (!err) {
        return null
      }
      return {
        name: err.name,
        message: err.message
      }
    },
    trackInfo (track) {
      if (!track) {
        return null
      }
      return {
        kind: track.kind,
        label: track.label,
        readyState: track.readyState,
        enabled: track.enabled,
        settings: track.getSettings ? track.getSettings() : {},
        capabilities: track.getCapabilities ? track.getCapabilities() : {}
      }
    },
    reportCameraEvent (eventType, payload = {}) {
      reportAHSCameraEvent({
        ...this.taskParams,
        pointId: this.current.pointId || '',
        pointName: this.current.name || '',
        eventType,
        message: this.cameraReportMessage(payload),
        userAgent: navigator.userAgent || '',
        isSecureContext: !!window.isSecureContext,
        hasMediaDevices: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
        currentCameraDeviceId: this.currentCameraDeviceId,
        selectedCameraDeviceId: this.selectedCameraDeviceId,
        cameraDeviceCount: this.cameraDeviceOptions.length,
        flashOn: this.flashOn,
        payload: this.sanitizeCameraReportPayload(payload)
      }).catch(() => {})
    },
    cameraReportMessage (payload) {
      const error = payload && payload.error
      if (error && error.message) {
        return error.message
      }
      return (payload && payload.message) || ''
    },
    sanitizeCameraReportPayload (value, depth = 0) {
      if (value == null) {
        return value
      }
      if (depth > 3) {
        return '[depth-limit]'
      }
      if (typeof value === 'string') {
        if (/^data:image\//.test(value) || value.length > 500) {
          return `${value.slice(0, 500)}...(truncated)`
        }
        return value
      }
      if (typeof value !== 'object') {
        return value
      }
      if (Array.isArray(value)) {
        return value.slice(0, 20).map((item) => this.sanitizeCameraReportPayload(item, depth + 1))
      }
      return Object.keys(value).slice(0, 30).reduce((result, key) => {
        if (/token|imgB64|base64|dataURL|dataUrl|file/i.test(key)) {
          result[key] = '[redacted]'
          return result
        }
        result[key] = this.sanitizeCameraReportPayload(value[key], depth + 1)
        return result
      }, {})
    },
    selectPoint (index) {
      this.index = index
      this.$nextTick(() => {
        this.resumeCamera()
      })
    },
    isMustPoint (item) {
      return item && (item.must === true || item.must === 1 || item.must === '1')
    },
    previewExampleImage () {
      const image = this.current && (this.current.exampleImg || this.current.stickFigureImg)
      if (!image) {
        return
      }
      ImagePreview([image])
    },
    goBack () {
      this.leaveCameraPage()
    },
    async takePicture () {
      const video = this.$refs.video
      const canvas = this.$refs.canvas
      if (!video || !canvas || !video.videoWidth) {
        this.takeFallbackPhoto()
        return
      }
      await this.waitCameraStable()
      drawZoomedVideoFrame(video, canvas, this.zoomValue, this.zoomHardware)
      this.setCurrentFile(canvas.toDataURL('image/jpeg', 0.92))
    },
    waitCameraStable () {
      return new Promise((resolve) => {
        setTimeout(resolve, CAMERA_CAPTURE_STABLE_DELAY)
      })
    },
    takeFallbackPhoto () {
      if (this.takeNativePhoto()) {
        return
      }
      this.openSystemCamera()
    },
    takeNativePhoto () {
      if (this.nativeCameraWaiting) {
        return true
      }
      const callbackName = this.registerNativePhotoCallback()
      const payload = {
        callbackName,
        fixedCallbackName: NATIVE_PHOTO_CALLBACK,
        pointId: this.current.pointId || '',
        pointName: this.current.name || '',
        capture: true,
        source: 'ahs'
      }
      const called = this.callNativeBridge('takePhoto', payload)
      if (!called) {
        this.reportCameraEvent('native_camera_unavailable', {
          action: 'takePhoto',
          bridgeNames: NATIVE_CAMERA_BRIDGE_NAMES
        })
        this.unregisterNativePhotoCallback()
        return false
      }
      this.nativeCameraWaiting = true
      this.nativePhotoTimer = setTimeout(() => {
        this.cameraLog('native photo timeout', { callbackName })
        this.reportCameraEvent('native_photo_timeout', {
          callbackName,
          timeout: NATIVE_PHOTO_TIMEOUT
        })
        this.unregisterNativePhotoCallback()
      }, NATIVE_PHOTO_TIMEOUT)
      this.cameraLog('native takePhoto called', payload)
      return true
    },
    registerNativePhotoCallback () {
      this.unregisterNativePhotoCallback()
      const callbackName = `${NATIVE_PHOTO_CALLBACK}_${Date.now()}_${Math.floor(Math.random() * 10000)}`
      const handler = (payload) => this.handleNativePhotoTaken(payload)
      this.nativePhotoCallbackName = callbackName
      window[callbackName] = handler
      window[NATIVE_PHOTO_CALLBACK] = handler
      return callbackName
    },
    unregisterNativePhotoCallback () {
      if (this.nativePhotoTimer) {
        clearTimeout(this.nativePhotoTimer)
        this.nativePhotoTimer = null
      }
      if (this.nativePhotoCallbackName && window[this.nativePhotoCallbackName]) {
        try {
          delete window[this.nativePhotoCallbackName]
        } catch (err) {
          window[this.nativePhotoCallbackName] = undefined
        }
      }
      if (window[NATIVE_PHOTO_CALLBACK]) {
        try {
          delete window[NATIVE_PHOTO_CALLBACK]
        } catch (err) {
          window[NATIVE_PHOTO_CALLBACK] = undefined
        }
      }
      this.nativePhotoCallbackName = ''
      this.nativeCameraWaiting = false
    },
    handleNativePhotoTaken (payload) {
      this.cameraLog('native photo callback', { payload })
      const data = this.normalizeNativePayload(payload)
      const image = this.nativePhotoImage(data)
      this.unregisterNativePhotoCallback()
      if (!image) {
        this.reportCameraEvent('native_photo_empty', {
          message: (data && (data.message || data.errorMsg)) || '',
          payload: data
        })
        Toast((data && (data.message || data.errorMsg)) || '原生拍照未返回图片')
        return
      }
      this.setCurrentFile(image)
    },
    normalizeNativePayload (payload) {
      if (typeof payload === 'string') {
        try {
          return JSON.parse(payload)
        } catch (err) {
          return { imageBase64: payload }
        }
      }
      return payload || {}
    },
    nativePhotoImage (data) {
      const payload = data.data && typeof data.data === 'object' ? data.data : data
      const image = payload.imageBase64 || payload.base64 || payload.dataURL || payload.dataUrl || payload.imageUrl || payload.url || ''
      if (!image) {
        return ''
      }
      if (/^(https?:)?\/\//.test(image) || /^data:image\//.test(image)) {
        return image
      }
      return `data:image/jpeg;base64,${image}`
    },
    openSystemCamera () {
      const input = this.$refs.systemCamera || this.$refs.album
      input.click()
    },
    onAlbumChange (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) {
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        this.setCurrentFile(reader.result)
      }
      reader.readAsDataURL(file)
    },
    setCurrentFile (file) {
      const list = [...this.list]
      const requestId = file ? `${Date.now()}-${this.index}` : ''
      list[this.index] = {
        ...list[this.index],
        file,
        imageUrl: '',
        said: '',
        status: file ? 'uploading' : 'pending',
        errorMsg: '',
        detectionBox: null,
        originalBox: null,
        uploadRequestId: requestId
      }
      this.list = list
      if (file) {
        this.processPointImage(this.index, file, requestId)
      }
    },
    retake () {
      this.setCurrentFile('')
      this.$nextTick(() => {
        this.resumeCamera()
      })
    },
    nextPoint () {
      if (this.hasNext) {
        this.index += 1
        return
      }
      this.finishTakePhoto()
    },
    async loadCameraPoints () {
      let data
      try {
        data = JSON.parse(sessionStorage.getItem(CAMERA_POINTS_KEY) || '')
      } catch (err) {
        data = null
      }
      if (!data || data.internalTaskNo !== this.internalTaskNo || !Array.isArray(data.points) || !data.points.length) {
        await this.loadCameraPointsFromContext()
        return
      }
      this.cameraTaskInfo = data
      this.list = this.normalizeCameraPoints(data.points)
    },
    async loadCameraPointsFromContext () {
      if (!this.internalTaskNo || !this.token) {
        if (this.cameraDebugEnabled) {
          this.list = this.normalizeCameraPoints(MOCK_POINTS)
          return
        }
        Toast('任务信息缺失，请从入口页重新进入')
        return
      }
      try {
        const res = await queryAHSContext(this.taskParams)
        if (res.code !== 200) {
          throw new Error(res.resultMessage || '查询任务点位失败')
        }
        const data = res.data || {}
        this.cameraTaskInfo = {
          internalTaskNo: data.internalTaskNo || this.internalTaskNo,
          taskNo: data.taskNo || this.partnerTaskNo,
          token: this.token
        }
        const points = this.mergeUploadedPoints(this.normalizeContextPoints(data.pointList), data.uploadedPoints || [])
        this.list = this.normalizeCameraPoints(points)
      } catch (err) {
        Toast(err.message || '查询任务点位失败')
      }
    },
    normalizeContextPoints (pointList) {
      const source = (pointList && pointList.totalPointInfos) || []
      return source.map((item, index) => ({
        ...item,
        index,
        pointId: String(item.pointId || item.pointID || ''),
        name: item.name || item.pointName || `点位${index + 1}`,
        must: Number(item.must || (item.necessity ? 1 : 2)),
        file: '',
        imageUrl: '',
        said: '',
        status: 'pending',
        errorMsg: ''
      }))
    },
    mergeUploadedPoints (points, uploadedPoints) {
      const uploadedMap = uploadedPoints.reduce((map, item) => {
        map[String(item.pointId)] = item
        return map
      }, {})
      if (!points.length) {
        return uploadedPoints.map((item, index) => ({
          index,
          pointId: String(item.pointId || ''),
          name: item.pointName || `点位${index + 1}`,
          must: 2,
          file: item.imageUrl,
          imageUrl: item.imageUrl,
          said: item.said,
          status: item.said ? 'success' : 'pending',
          errorMsg: ''
        }))
      }
      return points.map((point) => {
        const uploaded = uploadedMap[String(point.pointId)]
        if (!uploaded) {
          return point
        }
        return {
          ...point,
          file: uploaded.imageUrl,
          imageUrl: uploaded.imageUrl,
          said: uploaded.said,
          status: uploaded.said ? 'success' : 'pending'
        }
      })
    },
    normalizeCameraPoints (points) {
      return points.map((item, index) => ({
        ...MOCK_POINTS[index % MOCK_POINTS.length],
        ...item,
        index: typeof item.index === 'number' ? item.index : index,
        pointId: String(item.pointId || ''),
        file: item.file || '',
        imageUrl: item.imageUrl || '',
        said: item.said || '',
        status: item.status || 'pending',
        errorMsg: item.errorMsg || ''
      }))
    },
    finishTakePhoto () {
      if (!this.takenCount) {
        Toast('请先拍摄鉴定点图片')
        return
      }
      if (!this.isReadyToFinish) {
        if (this.missingMustCount) {
          Toast(`还有 ${this.missingMustCount} 个必拍点位未拍摄`)
          return
        }
        if (this.processingMustCount) {
          Toast('必拍点位正在鉴定中，请稍后')
          return
        }
        if (this.failedMustCount) {
          Toast('有必拍点位鉴定失败，请重拍')
          return
        }
        if (this.pendingMustResultCount) {
          Toast('必拍点位未完成鉴定，请重拍')
          return
        }
        Toast('请先完成必拍点位')
        return
      }
      if (!this.internalTaskNo || !this.token) {
        Toast('任务信息缺失，请返回重试')
        return
      }
      const files = this.list
        .filter((item) => item.file && item.said)
        .map((item, index) => ({
          index: typeof item.index === 'number' ? item.index : index,
          file: item.file,
          imageUrl: item.imageUrl || '',
          said: item.said || '',
          status: item.status || 'pending',
          detectionBox: item.detectionBox,
          originalBox: item.originalBox
        }))
      try {
        sessionStorage.setItem(CAMERA_RESULT_KEY, JSON.stringify({
          internalTaskNo: this.internalTaskNo,
          files
        }))
      } catch (err) {}
      this.leaveCameraPage()
    },
    leaveCameraPage () {
      if (this.enteredFromAccess) {
        this.$router.back()
        return
      }
      this.$router.replace({
        path: '/service-provider-access',
        query: this.accessPageQuery()
      })
    },
    accessPageQuery () {
      const query = {
        internalTaskNo: this.internalTaskNo,
        token: this.token
      }
      if (this.partnerTaskNo) {
        query.partnerTaskNo = this.partnerTaskNo
        query.taskNo = this.partnerTaskNo
      } else if (this.internalTaskNo) {
        query.taskNo = this.internalTaskNo
      }
      if (this.$route.query.debug === '1') {
        query.debug = '1'
      }
      if (this.$route.query.cameraDebug === '1' || this.$route.query.debugCamera === '1') {
        query.cameraDebug = '1'
      }
      return query
    },
    async processPointImage (index, file, requestId) {
      if (!this.internalTaskNo || !this.token) {
        this.updatePointIfCurrent(index, requestId, {
          status: 'failed',
          errorMsg: '任务信息缺失，请返回重试'
        })
        Toast('任务信息缺失，请返回重试')
        return
      }
      try {
        const imageUrl = await this.uploadPointImage(file)
        if (!this.isCurrentRequest(index, requestId)) {
          return
        }
        this.updatePointIfCurrent(index, requestId, {
          imageUrl,
          status: 'singleing'
        })
        await this.submitSingle(index, imageUrl, requestId)
      } catch (err) {
        this.updatePointIfCurrent(index, requestId, {
          status: 'failed',
          errorMsg: err.message || '鉴定失败，请重拍'
        })
        Toast(err.message || '鉴定失败，请重拍')
      }
    },
    async uploadPointImage (file) {
      if (/^https?:\/\//.test(file)) {
        return file
      }
      const res = await uploadAHSImage({
        ...this.taskParams,
        imgB64: this.stripBase64Prefix(file)
      })
      if (res.code !== 200 || !res.data || !res.data.imageUrl) {
        throw new Error((res && res.resultMessage) || '图片上传失败')
      }
      return res.data.imageUrl
    },
    async submitSingle (index, imageUrl, requestId) {
      const point = this.list[index]
      const res = await submitAHSSingle({
        ...this.taskParams,
        pointId: point.pointId,
        pointName: point.name,
        imageUrl,
        payload: {
          source: 'ahs'
        }
      })
      const data = res.data || {}
      const errorCode = Number(data.error_code || data.errorCode || 0)
      if (res.code !== 200 || errorCode !== 0) {
        throw new Error(this.singleErrorMessage(data, res.resultMessage, '单点鉴定失败'))
      }
      if (data.detection === false) {
        throw new Error(this.singleErrorMessage(data, '', '图片未检测到有效鉴定点'))
      }
      if (!data.said) {
        throw new Error('单点鉴定未返回 said')
      }
      this.updatePointIfCurrent(index, requestId, {
        imageUrl,
        said: data.said,
        status: 'success',
        errorMsg: '',
        detectionBox: data.detection_box || data.detectionBox,
        originalBox: data.original_box || data.originalBox
      })
    },
    updatePointIfCurrent (index, requestId, patch) {
      if (!this.isCurrentRequest(index, requestId)) {
        return
      }
      const point = this.list[index]
      const list = [...this.list]
      list[index] = { ...point, ...patch }
      this.list = list
    },
    isCurrentRequest (index, requestId) {
      const point = this.list[index]
      return !!point && point.uploadRequestId === requestId
    },
    stripBase64Prefix (dataURL) {
      return String(dataURL || '').replace(/^data:image\/\w+;base64,/, '')
    },
    singleErrorMessage (data, fallback, defaultMessage) {
      const message = (data && (data.error_msg || data.errorMsg)) || fallback || ''
      return !message || /^success$/i.test(String(message)) ? defaultMessage : message
    },
    callNativeBridge (action, payload = {}) {
      const data = { action, ...payload }
      for (const name of NATIVE_CAMERA_BRIDGE_NAMES) {
        const bridge = window[name]
        if (bridge && this.callNativeBridgeObject(bridge, action, data, name)) {
          return true
        }
        const handler = window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[name]
        if (handler && handler.postMessage) {
          try {
            handler.postMessage(data)
            this.cameraLog('native webkit bridge called', { bridge: name, action, data })
            return true
          } catch (err) {
            this.cameraLog('native webkit bridge failed', { bridge: name, action, error: this.errorInfo(err) })
            this.reportCameraEvent('native_bridge_failed', {
              bridge: name,
              action,
              error: this.errorInfo(err)
            })
          }
        }
      }
      const jsBridge = window.JSBridge || window.WebViewJavascriptBridge
      if (jsBridge) {
        if (typeof jsBridge.call === 'function') {
          try {
            jsBridge.call(action, data)
            this.cameraLog('native JSBridge.call called', { action, data })
            return true
          } catch (err) {
            this.cameraLog('native JSBridge.call failed', { action, error: this.errorInfo(err) })
            this.reportCameraEvent('native_bridge_failed', {
              bridge: 'JSBridge.call',
              action,
              error: this.errorInfo(err)
            })
          }
        }
        if (typeof jsBridge.callHandler === 'function') {
          try {
            jsBridge.callHandler(action, data)
            this.cameraLog('native JSBridge.callHandler called', { action, data })
            return true
          } catch (err) {
            this.cameraLog('native JSBridge.callHandler failed', { action, error: this.errorInfo(err) })
            this.reportCameraEvent('native_bridge_failed', {
              bridge: 'JSBridge.callHandler',
              action,
              error: this.errorInfo(err)
            })
          }
        }
      }
      return false
    },
    callNativeBridgeObject (bridge, action, data, bridgeName) {
      const methodNames = [action]
      if (action === 'toggleTorch') {
        methodNames.push('setTorch', 'toggleFlash', 'setFlash')
      }
      if (action === 'takePhoto') {
        methodNames.push('openCamera', 'openNativeCamera', 'takePicture')
      }
      for (const methodName of methodNames) {
        if (typeof bridge[methodName] !== 'function') {
          continue
        }
        try {
          bridge[methodName](JSON.stringify(data))
          this.cameraLog('native bridge called', { bridge: bridgeName, methodName, data })
          return true
        } catch (jsonErr) {
          try {
            bridge[methodName](data)
            this.cameraLog('native bridge called with object', { bridge: bridgeName, methodName, data })
            return true
          } catch (objectErr) {
            this.cameraLog('native bridge failed', {
              bridge: bridgeName,
              methodName,
              jsonError: this.errorInfo(jsonErr),
              objectError: this.errorInfo(objectErr)
            })
            this.reportCameraEvent('native_bridge_failed', {
              bridge: bridgeName,
              methodName,
              jsonError: this.errorInfo(jsonErr),
              objectError: this.errorInfo(objectErr)
            })
          }
        }
      }
      return false
    },
    callNativeTorch (on) {
      return this.callNativeBridge('toggleTorch', {
        on,
        enabled: on,
        source: 'ahs'
      })
    },
    currentVideoTrack () {
      return this.stream && this.stream.getVideoTracks()[0]
    },
    trackHasTorch (track) {
      return !!(track && track.getCapabilities && track.getCapabilities().torch)
    },
    torchCameraScore (device) {
      const label = String(device && device.label || '').toLowerCase()
      let score = 0
      if (/back|rear|environment|后|背/.test(label)) score += 3
      if (/camera2?\s*0/.test(label)) score += 3
      if (/camera2?\s*2/.test(label)) score += 1
      if (/tele|wide|ultra|macro/.test(label)) score -= 3
      if (/front|user|前/.test(label)) score -= 10
      return score
    },
    async applyTrackTorch (track, on) {
      if (!this.trackHasTorch(track)) {
        return false
      }
      try {
        await track.applyConstraints({ advanced: [{ torch: on }] })
        this.flashOn = on
        return true
      } catch (err) {
        this.reportCameraEvent('torch_failed', {
          error: this.errorInfo(err),
          track: this.trackInfo(track)
        })
        return false
      }
    },
    async attachCameraStream (stream) {
      this.stream = stream
      await this.syncCurrentCameraDevice(stream)
      await this.$nextTick()
      const video = this.$refs.video
      if (!video) {
        return
      }
      video.muted = true
      video.setAttribute('playsinline', 'true')
      video.setAttribute('webkit-playsinline', 'true')
      video.srcObject = stream
      this.playVideo()
      this.applyAutoFocus()
      this.bindCameraZoom(stream)
    },
    async switchToTorchCamera () {
      if (this.isCameraStarting) {
        return false
      }
      if (!this.cameraDeviceOptions.length) {
        await this.refreshCameraDevices()
      }
      const candidates = this.cameraDeviceOptions
        .filter((device) => device.deviceId && device.deviceId !== this.currentCameraDeviceId)
        .sort((a, b) => this.torchCameraScore(b) - this.torchCameraScore(a))
      for (let i = 0; i < candidates.length; i += 1) {
        const device = candidates[i]
        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: device.deviceId },
              width: { ideal: 1920 },
              height: { ideal: 1080 }
            },
            audio: false
          })
          const track = stream.getVideoTracks()[0]
          if (!this.trackHasTorch(track)) {
            stream.getTracks().forEach((item) => item.stop())
            continue
          }
          this.stopCamera()
          this.selectedCameraDeviceId = device.deviceId
          this.savePreferredCameraDevice(device.deviceId)
          await this.attachCameraStream(stream)
          this.reportCameraEvent('torch_switched_camera', {
            deviceId: device.deviceId,
            label: device.label,
            index: i
          })
          return true
        } catch (err) {
          if (stream) {
            stream.getTracks().forEach((item) => item.stop())
          }
          this.reportCameraEvent('torch_switch_camera_failed', {
            deviceId: device.deviceId,
            label: device.label,
            error: this.errorInfo(err)
          })
        }
      }
      return false
    },
    async toggleFlash () {
      const next = !this.flashOn
      if (this.callNativeTorch(next)) {
        this.flashOn = next
        return
      }
      if (await this.applyTrackTorch(this.currentVideoTrack(), next)) {
        return
      }
      if (next && await this.switchToTorchCamera()) {
        if (await this.applyTrackTorch(this.currentVideoTrack(), true)) {
          return
        }
      }
      const track = this.currentVideoTrack()
      this.reportCameraEvent('torch_unsupported', {
        reason: track ? 'torch_capability_false' : 'track_or_capabilities_missing',
        hasTrack: !!track,
        capabilities: track && track.getCapabilities ? track.getCapabilities() : {},
        track: this.trackInfo(track)
      })
      Toast('当前环境不支持补光')
    }
  }
}
</script>

<style lang="stylus" src="./take-photo.styl"></style>
